import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, TrendingDown } from "lucide-react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/api/client";
import { fmtMoney } from "@/utils/money";

type Item = {
    invoice_id: string; invoice_number: string; person_id: string; person_name: string;
    due_date: string; days_overdue: number; effective_total_minor: number; effective_paid_minor: number;
    debt_minor: number; currency: string; bucket: "current" | "0_30" | "31_60" | "60_plus";
};
type Debt = { buckets: Record<string, number>; items: Item[]; total_debt_minor: number };

export default function DebtPage() {
    const { t } = useTranslation();
    const [data, setData] = useState<Debt | null>(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try { const { data } = await api.get("/finance/debt"); setData(data); }
        catch (e: any) { setErr(formatApiError(e)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    if (loading) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin"/></div>;
    if (err) return <div className="rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }} data-testid="debt-error">{err}</div>;
    if (!data) return null;

    const buckets: Array<{ key: string; color: string }> = [
        { key: "current", color: "var(--info-fg)" },
        { key: "0_30", color: "var(--warning-fg)" },
        { key: "31_60", color: "var(--warning-fg)" },
        { key: "60_plus", color: "var(--error-fg)" },
    ];

    return (
        <div className="space-y-6" data-testid="debt-page">
            <h1 className="text-3xl font-semibold" style={{ color: "var(--text)" }} data-testid="page-title">{t("finance.debt.title")}</h1>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                {buckets.map(({ key, color }) => (
                    <div key={key} className="surface-card p-4" data-testid={`bucket-${key}`}>
                        <div className="text-xs mb-2 flex items-center gap-1" style={{ color: "var(--text-muted)" }}><TrendingDown size={12} style={{ color }}/>{t(`finance.debt.bucket.${key}` as any)}</div>
                        <div className="text-2xl font-semibold tabular-nums" style={{ color }}>{fmtMoney(data.buckets[key] || 0, "AZN")}</div>
                    </div>
                ))}
            </div>
            <div className="surface-card p-4 flex justify-between items-center" data-testid="debt-total">
                <span className="text-sm" style={{ color: "var(--text-muted)" }}>{t("finance.debt.total")}</span>
                <span className="text-2xl font-semibold tabular-nums" style={{ color: "var(--error-fg)" }}>{fmtMoney(data.total_debt_minor, "AZN")}</span>
            </div>
            <div className="surface-card overflow-hidden">
                {data.items.length === 0 ? (
                    <div className="p-8 text-center text-sm" style={{ color: "var(--text-muted)" }} data-testid="debt-empty">{t("common.empty")}</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead><tr className="text-left" style={{ background: "var(--input)", color: "var(--text-muted)" }}>
                            <th className="px-4 py-2 font-medium">№</th>
                            <th className="px-4 py-2 font-medium">{t("common.name")}</th>
                            <th className="px-4 py-2 font-medium">{t("finance.due_date")}</th>
                            <th className="px-4 py-2 font-medium text-right">{t("finance.debt.days_overdue")}</th>
                            <th className="px-4 py-2 font-medium text-right">{t("finance.debt")}</th>
                            <th className="px-4 py-2 font-medium">{t("finance.debt.bucket_label")}</th>
                        </tr></thead>
                        <tbody>
                            {data.items.map((it) => (
                                <tr key={it.invoice_id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`debt-row-${it.invoice_id}`}>
                                    <td className="px-4 py-3 font-mono text-xs" style={{ color: "var(--text)" }}>{it.invoice_number}</td>
                                    <td className="px-4 py-3"><Link to={`/people/${it.person_id}`} className="hover:underline" style={{ color: "var(--text)" }}>{it.person_name}</Link></td>
                                    <td className="px-4 py-3" style={{ color: "var(--text-muted)" }}>{it.due_date}</td>
                                    <td className="px-4 py-3 text-right tabular-nums" style={{ color: it.days_overdue > 0 ? "var(--error-fg)" : "var(--text-muted)" }}>{it.days_overdue}</td>
                                    <td className="px-4 py-3 text-right tabular-nums font-medium" style={{ color: "var(--error-fg)" }}>{fmtMoney(it.debt_minor, it.currency)}</td>
                                    <td className="px-4 py-3"><span className={`status-pill ${it.bucket === "60_plus" ? "status-error" : it.bucket === "current" ? "status-info" : "status-warning"}`}>{t(`finance.debt.bucket.${it.bucket}` as any)}</span></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
